/* eslint-disable max-statements */
import { useCallback } from 'react'
import { useMutation, useReactiveVar } from '@apollo/client'
import { VERIFY_WALLET_SIGNATURE } from 'api/auth/verify-wallet-signature'
import { reconnectWs } from 'app/apollo-middlewares'
import {
  isUserDetectedRegisteredVar,
  referrerIdVar,
  userNameVar,
  walletAddressVar,
  walletSignatureVar,
  wizardModeVar
} from '../store/wizard.store'
import { WizardMode } from '../types'
import { getVariablesToVerify } from '../utils/get-variables-to-verify'
import { notifyOnVerifyingError } from '../utils/notify'
import { resetWizardState } from '../utils/reset-wizard-state'
import { useUser } from './use-user'

export function useVerifyWallet() {
  const { refetchUser } = useUser()
  const wizardMode = useReactiveVar(wizardModeVar)
  const walletSignature = useReactiveVar(walletSignatureVar)
  const walletAddress = useReactiveVar(walletAddressVar)
  const walletUsername = useReactiveVar(userNameVar)
  const referrerId = useReactiveVar(referrerIdVar)
  const isUserDetectedRegistered = useReactiveVar(isUserDetectedRegisteredVar)

  const [verifyWalletSignature, { loading }] = useMutation(
    VERIFY_WALLET_SIGNATURE
  )

  // eslint-disable-next-line max-statements
  const verifyWallet = useCallback(async () => {
    if (!walletAddress || !walletSignature)
      throw new Error('Wallet data is missing')
    if (wizardMode === WizardMode.signUp && !walletUsername)
      throw new Error('Username is missing')

    const variables = getVariablesToVerify(
      wizardMode,
      walletAddress,
      walletSignature,
      walletUsername,
      referrerId,
      isUserDetectedRegistered
    )

    if (!variables) throw new Error('No variables to verify')

    verifyWalletSignature({
      variables,
      onError: e => {
        notifyOnVerifyingError(e)
        resetWizardState(wizardMode)
      },
      onCompleted: () => {
        refetchUser()
        reconnectWs()
      }
    })
  }, [
    verifyWalletSignature,
    refetchUser,
    referrerId,
    walletAddress,
    walletSignature,
    walletUsername,
    wizardMode,
    isUserDetectedRegistered
  ])

  return {
    loading,
    verifyWallet
  }
}
